import React, { Children, cloneElement, isValidElement } from "react";
import { motion, useReducedMotion } from "framer-motion";
import styles from "./textReveal.module.css";
import { EASE } from "../../../UTILS/ANIMATION/motionVariants";

export const WORD_STAGGER = 0.055;
const WORD_DURATION = 0.75;

const word = {
  hidden: { y: "108%", opacity: 0, filter: "blur(6px)" },
  visible: {
    y: "0%",
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: WORD_DURATION, ease: EASE },
    transitionEnd: { filter: "none" },
  },
};

// Splits plain strings into words. A child element whose content is a plain
// string (e.g. <em>possible.</em>) keeps its tag around each of its words.
const toWords = (children) => {
  const out = [];
  Children.forEach(children, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      String(child)
        .split(/\s+/)
        .filter(Boolean)
        .forEach((w) => out.push({ text: w }));
    } else if (isValidElement(child) && typeof child.props.children === "string") {
      child.props.children
        .split(/\s+/)
        .filter(Boolean)
        .forEach((w) => out.push({ text: w, wrap: child }));
    } else if (child !== null && child !== undefined && child !== false) {
      out.push({ node: child });
    }
  });
  return out;
};

const plainText = (children) =>
  Children.toArray(children)
    .map((c) => (typeof c === "string" || typeof c === "number" ? c : c?.props?.children ?? ""))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

/** Number of words, so following content can wait for the heading to land. */
export const countWords = (children) => toWords(children).length;

/** Seconds until the last word of `children` has finished arriving. */
export const revealDuration = (children, delay = 0) =>
  delay + Math.max(0, countWords(children) - 1) * WORD_STAGGER + WORD_DURATION;

// Reveals a heading word by word as it enters the viewport. Screen readers
// get the full sentence once through aria-label; the animated words are
// hidden from them. Each word is an unbreakable inline-block.
const TextReveal = ({
  as: Tag = "h2",
  children,
  className,
  id,
  delay = 0,
  stagger = WORD_STAGGER,
  onLoad = false,
  ...rest
}) => {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <Tag id={id} className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  const MotionTag = motion[Tag] || motion.div;
  const trigger = onLoad
    ? { initial: "hidden", animate: "visible" }
    : { initial: "hidden", whileInView: "visible", viewport: { once: true, amount: 0.3 } };

  const parts = toWords(children);
  return (
    <MotionTag
      id={id}
      className={className}
      aria-label={plainText(children)}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      {...trigger}
      {...rest}
    >
      {parts.map((p, i) => {
        if (p.node) return <React.Fragment key={i}>{p.node}</React.Fragment>;
        const inner = (
          <motion.span className={styles.inner} variants={word}>
            {p.text}
          </motion.span>
        );
        return (
          <React.Fragment key={i}>
            <span className={styles.mask} aria-hidden="true">
              {p.wrap ? cloneElement(p.wrap, {}, inner) : inner}
            </span>
            {i < parts.length - 1 ? " " : null}
          </React.Fragment>
        );
      })}
    </MotionTag>
  );
};

export default TextReveal;
